import {
  RtcIceOutcome,
  RtcIceServer,
  RtcPostOutcome,
  RtcSignalType,
} from './direct-call-rtc';

/**
 * US 37334 (Feature 37328 S6): the structural shapes RtcMesh needs from a peer connection and its host, so the
 * mesh runs on web, in a hosted web view and under node tests. Kept apart from the mesh to hold both under the
 * file-size gate.
 */
export interface RtcTrackLike {
  readonly kind: string;
}

export interface RtcSenderLike {
  replaceTrack(track: RtcTrackLike | null): Promise<void>;
}

export interface RtcDescriptionLike {
  type: string;
  sdp?: string;
}

export interface RtcPeerLike {
  onnegotiationneeded: (() => void) | null;
  onicecandidate: ((event: { candidate: unknown }) => void) | null;
  ontrack:
    | ((event: { track: RtcTrackLike; streams: readonly unknown[] }) => void)
    | null;
  oniceconnectionstatechange: (() => void) | null;
  readonly iceConnectionState: string;
  readonly signalingState: string;
  readonly localDescription: RtcDescriptionLike | null;
  setLocalDescription(description?: RtcDescriptionLike): Promise<void>;
  setRemoteDescription(description: RtcDescriptionLike): Promise<void>;
  addIceCandidate(candidate: unknown): Promise<void>;
  addTrack(track: RtcTrackLike, stream: unknown): RtcSenderLike;
  /** A receive-only transceiver lets a human with no camera or microphone still offer (S6c-1). */
  addTransceiver(kind: string, init: { direction: string }): unknown;
  getStats(): Promise<{
    forEach(cb: (report: Record<string, unknown>) => void): void;
  }>;
  close(): void;
}

/** The local media this human offers: an audio track, an optional camera track, and their stream. */
export interface RtcLocalMedia {
  stream: unknown;
  audio?: RtcTrackLike;
  video?: RtcTrackLike;
}

export interface RtcMediaState {
  audio: boolean;
  video: boolean;
  screen: boolean;
}

export interface RtcMeshOptions {
  /**
   * The call this mesh belongs to. The hub feed carries every conversation's signals, so one for any
   * other conversation (a late one, or a second open call) is dropped before it can build a peer.
   */
  conversationId: string;
  send(
    to: string,
    type: RtcSignalType,
    payload: Record<string, unknown> | null
  ): Promise<unknown>;
  createPeer(iceServers: RtcIceServer[]): RtcPeerLike;
  /** Resolves with the POST's outcome; only `sent` counts as reported, anything else is tried again. */
  reportOutcome(to: string, outcome: RtcIceOutcome): Promise<RtcPostOutcome>;
  onRemoteTrack(
    peer: string,
    track: RtcTrackLike,
    streams: readonly unknown[]
  ): void;
  onPeerLeft?(peer: string): void;
  onMediaState?(peer: string, state: RtcMediaState): void;
  /**
   * Waits between stats reads while a connected pair's nominated candidate pair is not listed yet. Default
   * {@link ICE_STATS_RETRY_MS}; tests pass zeros.
   */
  iceStatsRetryMs?: readonly number[];
  /** Clock for the stale-offer rule; tests pass their own. */
  now?(): number;
  /** Runs `fn` after `ms` and returns its cancel; default setTimeout. Tests pass one driven by their clock. */
  setTimer?(fn: () => void, ms: number): () => void;
}

/** Most ICE candidates kept per sender before its offer or our remote description arrives. */
export const MAX_EARLY_CANDIDATES = 64;
/** Backoff for re-reading stats when ICE is connected but no nominated pair is listed yet (7438 review). */
export const ICE_STATS_RETRY_MS: readonly number[] = [250, 500, 1000, 2000];
/** Signals kept from before start() (a member still opening its camera); the rest are dropped. */
export const MAX_PRESTART_SIGNALS = 512;
/**
 * A first offer unanswered this long went to a human who was not there (rtc/config lists every human in the call, present
 * or not): when they arrive and offer, the impolite side yields instead of ignoring them forever. Crossed offers of a real
 * glare meet within one round trip, far inside this (S6c-1).
 */
export const STALE_OFFER_MS = 10_000;

/** The SDP session id (the o= line's second field): one per peer connection, kept across its renegotiations. */
export function sdpSession(sdp: string | undefined): string | undefined {
  return sdp ? /^o=\S+\s+(\d+)\s/m.exec(sdp)?.[1] : undefined;
}

/** host, srflx, prflx or relay from the nominated pair's local candidate; unknown reads as failed. */
export async function selectedCandidateType(
  pc: RtcPeerLike
): Promise<RtcIceOutcome> {
  const reports: Record<string, unknown>[] = [];
  (await pc.getStats()).forEach((r) => reports.push(r));
  const pair = reports.find(
    (r) =>
      r['type'] === 'candidate-pair' &&
      r['state'] === 'succeeded' &&
      r['nominated'] === true
  );
  const local =
    pair && reports.find((r) => r['id'] === pair['localCandidateId']);
  const type = local?.['candidateType'];
  return type === 'host' ||
    type === 'srflx' ||
    type === 'prflx' ||
    type === 'relay'
    ? type
    : 'failed';
}

/** RtcMeshOptions.setTimer's default: a real timer, cancelled by the returned function. */
export function defaultSetTimer(fn: () => void, ms: number): () => void {
  const id = setTimeout(fn, ms);
  return () => clearTimeout(id);
}
